import React, { useContext } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { CircleCheck } from "lucide-react";
import Button from "common/Buttons/Button";
import IconWithFallback from "Global/IconWithFallback";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { DASHBOARD_PATH } from "common/constants.ts";
import { ICON_TYPES } from "@shared/common/constants.ts";
import { AccountFormContext } from "@shared/features/common/AccountForm.context.ts";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import { ExternalInvitationType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import InvitingUserSummary from "features/configuration/InviteExternalUsers/InvitingUserSummary.tsx";
import NewLookSuccessIllustration from "features/portfolio/CreateAccount/NewLook/NewLookSuccessIllustration";
import {
  stepDescription,
  stepTitle,
} from "features/onboarding/common/onboardingUi";
import {
  LogoTile,
  SectionLabel,
} from "features/portfolio/CreateAccount/NewLook/styles";

const NewLookExternalSuccess = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const history = useHistory();
  const { createdAccounts } = useContext(AccountFormContext)!;
  const { isWhitelabel, hasReadAccess, inviteData } = useContext(
    ExternalConnectAccountContext
  )!;
  const isSingleAccount = !createdAccounts || createdAccounts.length === 1;
  const brandPrefix = isWhitelabel ? "" : "Cornix ";

  return (
    <Wrapper>
      <NewLookSuccessIllustration width={148} height={111} />
      <Title>
        {isSingleAccount
          ? t("external_connect.success.title_one")
          : t("external_connect.success.title_other")}
      </Title>
      <Description>
        {isSingleAccount
          ? t("external_connect.success.description_one", { brandPrefix })
          : t("external_connect.success.description_other", { brandPrefix })}
      </Description>
      <Inviter>
        <InvitingUserSummary hideBg={true} />
      </Inviter>
      {!!createdAccounts?.length && (
        <Accounts>
          <SectionLabel>
            {isSingleAccount
              ? t("external_connect.success.connected_accounts_one")
              : t("external_connect.success.connected_accounts_other")}
          </SectionLabel>
          <List>
            {createdAccounts.map((account, index) => (
              <Row key={index}>
                <LogoTile $size={36}>
                  <IconWithFallback
                    iconType={ICON_TYPES.EXCHANGE}
                    coinOrExchangeName={account.exchange}
                    pxIconSize={30}
                    iconStyle={{ width: 30, height: 30 }}
                  />
                </LogoTile>
                <AccountText>
                  <AccountName>{account.client_name}</AccountName>
                  <Exchange>{account.exchange}</Exchange>
                </AccountText>
                <Connected>
                  <CircleCheck
                    size={16}
                    strokeWidth={NEW_LOOK_ICON_STROKE}
                    aria-hidden
                  />
                </Connected>
              </Row>
            ))}
          </List>
        </Accounts>
      )}
      <Note>
        {inviteData?.type === ExternalInvitationType.PERSONAL
          ? t("external_connect.success.close_tab")
          : t("external_connect.success.revoke_access")}
      </Note>
      {hasReadAccess && (
        <Button
          type="primary"
          $size="l"
          onClick={() => history.push(DASHBOARD_PATH)}
        >
          {isWhitelabel
            ? t("external_connect.success.view_account")
            : t("external_connect.success.continue_to_cornix")}
        </Button>
      )}
    </Wrapper>
  );
};

export default NewLookExternalSuccess;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 520px;
  padding-top: 8px;
  text-align: center;
`;
const Title = styled.h1`
  margin: 16px 0 8px;
  ${stepTitle};
`;
const Description = styled.p`
  margin: 0;
  ${stepDescription};
`;
const Inviter = styled.div`
  margin-top: 8px;
`;
const Accounts = styled.section`
  width: 100%;
  margin-top: 24px;
  padding: 20px;
  text-align: left;
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${(p) => p.theme.radii.card}px;
  background: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: ${(p) => p.theme.shadows.panel};
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  & + & {
    border-top: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  }
`;
const AccountText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;
const AccountName = styled.span`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
`;
const Exchange = styled.span`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12.5px;
  line-height: 18px;
`;
const Connected = styled.span`
  flex: none;
  display: inline-flex;
  color: ${(p) => p.theme.colors.green};
`;
const Note = styled.p`
  margin: 20px 0;
  max-width: 440px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
  line-height: 19px;
`;
